/**
 * Native bridges for the App Store build (iPhone, iPad, Mac via Catalyst).
 *
 * The app's own Capacitor plugin `AhpNative` (ios/App/App/AhpNativePlugin.swift)
 * provides print, share, durable storage and app info. This module wires it in
 * underneath the web code, so existing call sites keep working unchanged:
 *
 *   - window.print()      → native print sheet (Save as PDF on Mac, Save to Files on iOS)
 *   - navigator.share()   → native share sheet, with in-app URLs rewritten to the public site
 *   - saved hospitals / scenarios in localStorage → mirrored to UserDefaults, and
 *     restored from it at launch, because WKWebView storage can be purged by the OS
 *   - an offline banner while the device has no connection
 *   - menu bar / keyboard commands from the Mac app (ios AppDelegate posts
 *     `ahp-command` events)
 *
 * Installed once from main.tsx, only when running inside the native app.
 */
import { registerPlugin } from '@capacitor/core';

interface AhpNativePlugin {
  print(): Promise<void>;
  share(opts: { title?: string; text?: string; url?: string }): Promise<void>;
  get(opts: { key: string }): Promise<{ value: string | null }>;
  set(opts: { key: string; value: string | null }): Promise<void>;
  appInfo(): Promise<{ version: string; build: string }>;
}

export const AhpNative = registerPlugin<AhpNativePlugin>('AhpNative');

const PUBLIC_ORIGIN = 'https://americanhospitalprices.com';

// localStorage keys that hold what the user saved (hospitalStorage.ts,
// scenarioStorage.ts, FollowScenarioButton, familyStorage.ts). Mirrored to
// native storage. The luigis_ prefix is historical and shared by all brands.
const DURABLE_KEYS = new Set([
  'ahp_followed_hospitals',
  'luigis_followed_scenarios',
  'luigis_user_scenarios',
  'luigis_bookmarked_scenarios',
  'luigis_recent_searches',
  'luigis_family_members',
  'luigis_family_initialized',
]);

const isDurable = (key: string) => DURABLE_KEYS.has(key);

/** In-app links (capacitor://localhost/…) → the same page on the public site. */
export function publicUrl(url?: string): string | undefined {
  if (!url) return url;
  try {
    const u = new URL(url, window.location.href);
    if (u.protocol === 'capacitor:' || u.hostname === 'localhost') {
      return PUBLIC_ORIGIN + u.pathname + u.search + u.hash;
    }
    return u.toString();
  } catch {
    return url;
  }
}

async function restoreDurableStorage(): Promise<void> {
  try {
    const { value } = await AhpNative.get({ key: '__ahp_durable_index' });
    const keys: string[] = value ? JSON.parse(value) : [];
    for (const key of keys) {
      if (localStorage.getItem(key) !== null) continue;
      const stored = await AhpNative.get({ key });
      if (stored.value !== null) localStorage.setItem(key, stored.value);
    }
  } catch {
    // Native storage unavailable: the web copy still works for this session.
  }
}

function mirrorDurableStorage(): void {
  const index = new Set<string>();
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && isDurable(k)) index.add(k);
  }
  const saveIndex = () => void AhpNative.set({ key: '__ahp_durable_index', value: JSON.stringify([...index]) }).catch(() => {});
  // Seed native storage with whatever the web copy already holds.
  for (const k of index) void AhpNative.set({ key: k, value: localStorage.getItem(k) }).catch(() => {});
  saveIndex();

  const proto = Storage.prototype;
  const origSet = proto.setItem;
  const origRemove = proto.removeItem;
  proto.setItem = function (this: Storage, key: string, value: string) {
    origSet.call(this, key, value);
    if (this === localStorage && isDurable(key)) {
      if (!index.has(key)) {
        index.add(key);
        saveIndex();
      }
      void AhpNative.set({ key, value }).catch(() => {});
    }
  };
  proto.removeItem = function (this: Storage, key: string) {
    origRemove.call(this, key);
    if (this === localStorage && isDurable(key)) {
      index.delete(key);
      saveIndex();
      void AhpNative.set({ key, value: null }).catch(() => {});
    }
  };
}

function installPrint(): void {
  window.print = () => {
    void AhpNative.print().catch((e) => console.warn('[AhpNative] print failed', e));
  };
}

function installShare(): void {
  const share = async (data?: ShareData) => {
    await AhpNative.share({ title: data?.title, text: data?.text, url: publicUrl(data?.url) });
  };
  try {
    Object.defineProperty(navigator, 'share', { value: share, configurable: true, writable: true });
    Object.defineProperty(navigator, 'canShare', { value: () => true, configurable: true, writable: true });
  } catch {
    // Non-configurable in this WebView: leave the built-in share in place.
  }
}

function installOfflineBanner(): void {
  const el = document.createElement('div');
  el.setAttribute('role', 'status');
  el.textContent = "You're offline. Saved hospitals and bundled prices still work; other pages need a connection.";
  Object.assign(el.style, {
    position: 'fixed', left: '12px', right: '12px', top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
    zIndex: '10050', padding: '10px 14px', borderRadius: '12px', fontSize: '13px', lineHeight: '1.35',
    background: 'rgba(0,40,104,0.94)', color: '#fff', boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
    fontFamily: "'Instrument Sans', system-ui, sans-serif", display: 'none', textAlign: 'center',
  } as CSSStyleDeclaration);
  const sync = () => { el.style.display = navigator.onLine ? 'none' : 'block'; };
  window.addEventListener('online', sync);
  window.addEventListener('offline', sync);
  const mount = () => { document.body.appendChild(el); sync(); };
  if (document.body) mount(); else window.addEventListener('DOMContentLoaded', mount, { once: true });
}

/** Router-agnostic navigation that React Router's BrowserRouter picks up. */
function go(path: string): void {
  if (window.location.pathname + window.location.search === path) return;
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function focusSearch(): void {
  go('/search');
  let tries = 0;
  const t = window.setInterval(() => {
    const input = document.querySelector<HTMLInputElement>('main input[type="search"], main input[type="text"], input[type="search"]');
    if (input || ++tries > 20) {
      window.clearInterval(t);
      input?.focus();
      input?.select();
    }
  }, 100);
}

function installCommands(): void {
  window.addEventListener('ahp-command', (e) => {
    const cmd = (e as CustomEvent<string>).detail;
    switch (cmd) {
      case 'search': return focusSearch();
      case 'scenarios': return go('/scenarios');
      case 'rankings': return go('/hospital-rankings');
      case 'saved': return go('/followed');
      case 'nearMe': return go('/near-me');
      case 'methodology': return go('/data-methodology');
      case 'privacy': return go('/privacy');
      case 'contact': return go('/contact');
      case 'settings': return go('/settings');
      case 'print': return window.print();
      case 'share': return void navigator.share?.({ title: document.title, url: window.location.href });
      case 'back': return window.history.back();
      case 'forward': return window.history.forward();
      default: return;
    }
  });
}

let installed = false;

/** Wire native print/share/storage/menus under the web app. Native only. */
export async function installNativeBridges(): Promise<void> {
  if (installed) return;
  installed = true;
  installPrint();
  installShare();
  installCommands();
  installOfflineBanner();
  await restoreDurableStorage();
  mirrorDurableStorage();
}
